import ModuleBase from '../shared/ModuleBase.js';

/**
 * BlogFilter — AJAX-Aufwertung der server-seitigen Blog-Kategorie-Filter & Pagination.
 *
 * Progressive Enhancement: Ohne JS bleiben es normale Links (Full Reload, SEO/Pagination intakt).
 * Mit JS werden Klicks auf Filter-Chips (.blog-filter a) und Pager-Links (.blog-pager a)
 * abgefangen, die Ziel-URL per fetch geholt und nur der innere Bereich der .blog-listing
 * weich ausgetauscht (Fade) — inkl. aktivem Chip-State, Grid und Pagination.
 */
export default class BlogFilter extends ModuleBase {
    constructor(el) {
        super(el);
        this._controller = null;
        this._init();
    }

    _init() {
        // Ausgangs-URL als eigenen History-State stempeln, damit der Zurück-Button
        // auch auf die initiale Seite per AJAX zurückschwenkt (sonst e.state === null).
        this._baseUrl = window.location.href;
        window.history.replaceState({ blogFilter: true }, '', this._baseUrl);

        // Klick-Delegation am persistenten Container — übersteht innerHTML-Swap.
        this.$el.addEventListener('click', (e) => this._onClick(e));
        // Browser-Zurück/Vor
        window.addEventListener('popstate', (e) => {
            if (e.state && e.state.blogFilter) {
                this._load(window.location.href, false, false);
            }
        });
    }

    _onClick(e) {
        // Modifier/Mittelklick → Browser-Default (neuer Tab etc.)
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            return;
        }

        const link = e.target.closest('.blog-filter a, .blog-pager a');
        if (!link || !this.$el.contains(link)) return;

        const href = link.getAttribute('href');
        if (!href) return;

        // Nur interne Links abfangen
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;

        e.preventDefault();
        if (url.href === window.location.href) return;

        const isPager = !!link.closest('.blog-pager');
        this._load(url.href, true, isPager);
    }

    async _load(href, push, scroll) {
        // Laufende Anfrage abbrechen
        if (this._controller) this._controller.abort();
        this._controller = new AbortController();

        this.$el.classList.add('is-loading');

        try {
            const res = await fetch(href, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                signal: this._controller.signal,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const html = await res.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('.blog-listing');
            if (!fresh) {
                // Unerwartete Antwort → harter Fallback auf normale Navigation
                window.location.href = href;
                return;
            }

            this.$el.innerHTML = fresh.innerHTML;
            this._controller = null;

            if (push) {
                window.history.pushState({ blogFilter: true }, '', href);
            }

            if (scroll) {
                this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            // Netzwerk-/Parse-Fehler → normale Navigation als Fallback
            window.location.href = href;
        } finally {
            this.$el.classList.remove('is-loading');
        }
    }
}
